import { ref, onUnmounted, type Ref } from 'vue';
import type { IDeferred } from '../utils/deferred';

export interface UseDeferredReturn<T> {
    /**
     * 当前值（响应式）
     */
    value: Ref<T | undefined>

    /**
     * 是否正在加载中（还没有获取到值）
     */
    isLoading: Ref<boolean>

    /**
     * 手动刷新值
     */
    refresh: () => Promise<void>
}

/**
 * 将 Deferred 对象转换为响应式的 Vue composable
 * @param deferred Deferred 对象
 * @returns 响应式的值和加载状态
 */
export function useDeferred<T>(deferred: IDeferred<T>): UseDeferredReturn<T> {
    const value = ref<T | undefined>(deferred.getValue()) as Ref<T | undefined>;
    const isLoading = ref(!deferred.hasValue);

    // 订阅 deferred 的变化
    const unsubscribe = deferred.onChange((newValue) => {
        value.value = newValue;
        isLoading.value = false;
    });

    // 如果还没有值，等待 promise
    if (!deferred.hasValue) {
        deferred.promise
            .then((v) => {
                value.value = v;
                isLoading.value = false;
            })
            .catch((error) => {
                console.error('[useDeferred] Failed to load value:', error);
                isLoading.value = false;
            });
    }

    // 组件卸载时取消订阅
    onUnmounted(() => {
        unsubscribe();
    });

    // 手动刷新值
    const refresh = async () => {
        isLoading.value = true;
        try {
            const newValue = await deferred.promise;
            value.value = newValue;
        } catch (error) {
            console.error('[useDeferred] Failed to refresh value:', error);
        } finally {
            isLoading.value = false;
        }
    };

    return {
        value,
        isLoading,
        refresh,
    };
}
